// WorkbenchScreen (TEMPLATES.md): one main region, and beside it from a tablet
// up one side panel the screen switches (DIRECTION U21). My Team: the lineup,
// and beside it the free agents, the waivers, the trades, the weeks ahead or
// the news, one at a time.
//
// TWO COLUMNS, NEVER THREE. Three columns of lists side by side read as a
// wall of lists; what sits beside the main region is switched, not added. A
// phone shows the main region alone, as one screen that scrolls (UX-38), and
// the screen offers its side panels there as doors to their own screens.
//
// THE SIDE PANEL CAN TAKE THE ROOM (`room`, B-400): the main region narrows
// to a dock and the panel takes the rest. Still two columns: the dock is the
// main region's edge, with no figures, not a third list.
//
// Drawn through the kit (LOOKS.md Part 7): the ratio is a class.

import { useState, type ReactNode } from 'react';
import { opensAsSheet } from '@fsp/theme';
import { Panel, ViewSwitch, type PanelView } from '../kit/Panel';
import { useDetail } from '../data/detail';
import { useTier } from '../shell/useTier';

export interface WorkbenchMain {
  title: string;
  /** A quiet figure on the right of the main region's head: the lineup's total. */
  meta?: ReactNode;
  content: ReactNode;
  /** The every-column body, which grows the zen/spreadsheet switch. */
  sheet?: ReactNode;
}

export function WorkbenchScreen({
  status,
  main,
  side,
  room = false,
}: {
  /** Over both columns: on a phone it scrolls away with the list, wider it stays. */
  status?: ReactNode;
  main: WorkbenchMain;
  /** The side panel, a `Panel` the screen draws; ignored on a phone. */
  side?: ReactNode;
  /** The side panel has taken the room, and the main region is its dock. */
  room?: boolean;
}) {
  const tier = useTier();
  const detail = useDetail();
  const [view, setView] = useState<PanelView | null>(null);
  const shown: PanelView = view ?? (opensAsSheet(detail) ? 'sheet' : 'zen');

  if (tier === 'phone') {
    // THE SCREEN SCROLLS AS ONE (UX-38): the status scrolls away, and the
    // region's name, its figure and its view switch stick to the top.
    return (
      <div className="fsp-screen fsp-flow">
        {status ? <div className="fsp-pinned">{status}</div> : null}
        <div className="fsp-pinned fsp-pinned-row fsp-pinned-stick">
          <span className="fsp-cap fsp-split-title">{main.title}</span>
          {main.meta ? <span className="fsp-workbench-meta">{main.meta}</span> : null}
          {main.sheet !== undefined ? (
            <ViewSwitch view={shown} onChange={setView} title={main.title} />
          ) : null}
        </div>
        <div className="fsp-regions fsp-board-one">
          <section className="fsp-region" aria-label={main.title}>
            {main.sheet !== undefined && shown === 'sheet' ? main.sheet : main.content}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="fsp-screen">
      {status ? <div className="fsp-pinned">{status}</div> : null}
      <div
        className={`fsp-regions ${side ? (room ? 'fsp-workbench fsp-workbench-room' : 'fsp-workbench') : 'fsp-board-one'}`}
      >
        <Panel
          title={main.title}
          meta={main.meta}
          sheet={main.sheet}
          fill
          className={room ? 'fsp-dock-panel' : undefined}
        >
          {main.content}
        </Panel>
        {side ?? null}
      </div>
    </div>
  );
}
